import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import type { CSSProperties } from "react";
import type { SchemeTokens, TvCallView } from "@shared/types/foyer";
import { getBinding } from "../../../lib/binding";
import { bridge } from "../../../lib/foyerBridge";
import type { CallEndCause } from "./CallMediaSurface";
import CallVeil from "./CallVeil";
import {
  CALL_TYPE,
  callCard,
  callEyebrow,
  callTheme,
  videoPill,
  type CallTheme,
} from "./callTheme";

/**
 * IncomingCallOverlay - the global incoming-call ring (VC-27).
 *
 * Mounted by SchemeRouter, NOT by CareScheme, so it paints ABOVE whatever the
 * resident is on: the photo slideshow, the whiteboard, the radio, live TV - any
 * scheme screen. A ring a resident can only see on the launcher is half a
 * feature; the normal state is a resident watching something. (Ringing over a
 * NATIVE app that has taken the foreground is VC-28, not this.)
 *
 * The whole interaction is two keys and nothing else (design §4.1):
 *   • OK   answers  → mounts the media surface (VC-20).
 *   • Back declines → ends the session as `declined`.
 * No timeout confirmation, no nested menu. The ring auto-dismisses when the
 * caller cancels or the ring times out - those arrive as `call: null` from the
 * room call session (VC-18), which unmounts this component, so there is nothing
 * to clean up here beyond stopping the ringtone.
 *
 * Auto-answer (hospital, phase 5) is scaffolded: when `call.autoAnswer` is set
 * the ring becomes an announce screen with a gentle chime for `announceSeconds`,
 * after which it connects on its own. Back still refuses during the window.
 *
 * Stale-ring safety is inherited, not re-implemented: `useRoomCallSession`
 * reconciles on every (re)connect and never replays a `call_invite`, so a TV
 * that reconnects tomorrow cannot resurrect yesterday's ring (VC-18). This
 * component only ever reflects the `call` it is handed.
 *
 * Look: the scrim is opaque and universal (it has to survive a bright photo
 * behind it); the card on top of it is Hemvist - warm paper, deep green,
 * Figtree - built from the resolved scheme tokens SchemeRouter passes in. See
 * `callTheme.ts` for why that split, and why this file cannot simply call
 * `useScheme()`.
 */

/** Lazy - LiveKit is heavy and must not enter the bundle until a call is
 *  actually answered. Mounting it IS the `active` state (design §4.2). */
const CallMediaSurface = lazy(() => import("./CallMediaSurface"));

/** What the resident is looking at right now. Driven by the `call` prop plus a
 *  single local answer. */
type Phase = "ring" | "announce" | "connecting" | "active" | "failed";

/** The join credentials minted by `/call/:id/token`. */
interface JoinInfo {
  token: string;
  url: string;
}

function callHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const b = getBinding();
  if (b?.screenToken) headers["X-Screen-Token"] = b.screenToken;
  return headers;
}

function initialOf(name: string | null): string {
  return (name?.trim()[0] ?? "?").toUpperCase();
}

/**
 * A loud, asset-free ringtone / announce chime via WebAudio.
 *
 * We generate rather than ship a file so it is guaranteed loud enough to be
 * heard from a chair by someone hard of hearing (design brief) without a network
 * fetch between the invite and the first "brring". We deliberately do NOT touch
 * the TV's hardware volume or mute - that is the resident's own control and
 * overriding it would be worse than a quiet ring; the software gain here is our
 * only lever, set high.
 */
function useRingtone(active: boolean, mode: "ring" | "chime"): void {
  useEffect(() => {
    if (!active || typeof window === "undefined") return;
    const Ctor: typeof AudioContext | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;
    if (!Ctor) return;

    let ctx: AudioContext;
    try {
      ctx = new Ctor();
    } catch {
      return;
    }
    // On a kiosk WebView autoplay is allowed; resume() covers a suspended
    // context and is a no-op where it is already running.
    void ctx.resume?.().catch(() => {});

    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const master = ctx.createGain();
    master.gain.value = mode === "ring" ? 0.55 : 0.3;
    master.connect(ctx.destination);

    // A classic double-buzz for the ring (two close tones), a single soft tone
    // for the announce chime. `on` is the tone length, `gap` the silence after.
    const spec =
      mode === "ring"
        ? { freqs: [480, 620], on: 1.0, gap: 0.85 }
        : { freqs: [659.25], on: 0.45, gap: 1.6 };

    const burst = (): void => {
      if (stopped) return;
      const t0 = ctx.currentTime;
      const g = ctx.createGain();
      g.connect(master);
      // Attack/release envelope so each burst doesn't click.
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(1, t0 + 0.04);
      g.gain.setValueAtTime(1, t0 + spec.on - 0.08);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + spec.on);
      for (const f of spec.freqs) {
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.value = f;
        o.connect(g);
        o.start(t0);
        o.stop(t0 + spec.on + 0.03);
      }
      timer = setTimeout(burst, (spec.on + spec.gap) * 1000);
    };
    burst();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      try {
        master.disconnect();
      } catch {
        /* already torn down */
      }
      void ctx.close?.().catch(() => {});
    };
  }, [active, mode]);
}

export default function IncomingCallOverlay({
  call,
  onDismiss,
  onHold,
  tokens,
  previewPhase,
}: {
  /** The live inbound call (never a `room`-initiated one - SchemeRouter filters
   *  the TV's own outgoing call out before mounting us). */
  call: TvCallView;
  /** The premise's resolved scheme tokens, so operator colour overrides reach a
   *  window that is mounted outside the scheme provider. Omitted (tests, or a
   *  premise with no binding) falls back to the care manifest defaults. */
  tokens?: SchemeTokens;
  /**
   * Design-preview only: start on a given phase so the connecting veil and the
   * "could not connect" card can be looked at without engineering a real
   * failure. Set solely by App's `?scheme=care&call=…` branch; never by
   * SchemeRouter's live path, so a bound TV cannot be talked into it.
   */
  previewPhase?: Phase;
  /** Drop the ring locally (answered / declined / gone). The server remains the
   *  truth; the next reconcile corrects us either way. */
  onDismiss: (callId?: string) => void;
  /** Ask to stay mounted after the session is gone, because there is something
   *  on screen the resident has not acknowledged yet (a failed answer). Without
   *  it the explanation is unmounted together with the call it explains. */
  onHold?: () => void;
}) {
  const t = callTheme(tokens);
  const callId = call.callId;
  const name = call.caller.displayName?.trim() || "Inkommande samtal";
  const relation = call.caller.relation?.trim() || null;
  const avatarUrl = call.caller.avatarUrl;

  const [phase, setPhase] = useState<Phase>(
    previewPhase ?? (call.autoAnswer ? "announce" : "ring")
  );
  const join = useRef<JoinInfo | null>(null);
  // First-writer-wins guard: an answer, a decline, and an external state change
  // must never fight over the terminal action.
  const acted = useRef(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useRingtone(phase === "ring", "ring");
  useRingtone(phase === "announce", "chime");

  // ── Terminal actions ──────────────────────────────────────────────────────

  /**
   * Answering is TWO server steps, and the gap between them is where a resident
   * gets stranded: `/answer` flips the session to `active` (which is what makes
   * the relative's phone join the media room and start showing itself), and only
   * then does `/token` mint our own key to that room. If the second step fails
   * we have already told the caller we picked up.
   *
   * Every failure here used to be a bare `onDismiss` - the ring vanished, the TV
   * fell back to whatever was behind it, the resident saw the main menu and no
   * explanation, and the relative sat alone in a room nobody was coming to. That
   * is exactly the shape of the pilot failure on 2026-07-27.
   *
   * So a failure now does three things instead of one:
   *   1. says so on the TV (`failed` phase) rather than silently going home,
   *   2. HANGS UP the session we just answered, so the phone is released
   *      instead of ringing into an empty room, and
   *   3. logs which step failed with its status - the log ring buffer feeds
   *      `log_capture`, and on the box it lands in logcat, so the next
   *      occurrence is diagnosable from a desk.
   */
  const failAnswer = useCallback(
    (step: string, detail: string, answered: boolean) => {
      // eslint-disable-next-line no-console
      console.warn(
        `[call] answer failed at ${step}: ${detail} (call ${callId})`
      );
      // Stay on screen even once the session we are ending disappears.
      onHold?.();
      if (answered) {
        // We told the caller we picked up and cannot honour it - end the call so
        // their phone stops waiting on a room we will never enter.
        void fetch(`/api/foyer/call/${encodeURIComponent(callId)}/end`, {
          method: "POST",
          headers: callHeaders(),
        }).catch(() => {});
      }
      setPhase("failed");
    },
    [callId, onHold]
  );

  const answer = useCallback(async () => {
    if (acted.current) return;
    acted.current = true;
    setPhase("connecting");
    let answered = false;
    try {
      // 1) Move the session ringing → active (this is what tells the caller we
      //    picked up), then 2) mint our own join token for the media room.
      const a = await fetch(
        `/api/foyer/call/${encodeURIComponent(callId)}/answer`,
        { method: "POST", headers: callHeaders() }
      );
      if (!a.ok) {
        failAnswer("/answer", `HTTP ${a.status}`, false);
        return;
      }
      answered = true;
      const t = await fetch(
        `/api/foyer/call/${encodeURIComponent(callId)}/token`,
        { method: "POST", headers: callHeaders() }
      );
      if (!t.ok) {
        failAnswer("/token", `HTTP ${t.status}`, answered);
        return;
      }
      const body = (await t.json()) as { token?: string; url?: string };
      if (!body.token || !body.url) {
        failAnswer("/token", "response had no token/url", answered);
        return;
      }
      join.current = { token: body.token, url: body.url };
      setPhase("active");
    } catch (err) {
      failAnswer(
        answered ? "/token" : "/answer",
        err instanceof Error ? err.message : String(err),
        answered
      );
    }
  }, [callId, failAnswer]);

  const decline = useCallback(() => {
    if (acted.current) return;
    acted.current = true;
    void fetch(`/api/foyer/call/${encodeURIComponent(callId)}/decline`, {
      method: "POST",
      headers: callHeaders(),
    }).catch(() => {});
    onDismiss(callId);
  }, [callId, onDismiss]);

  /** Hang up an answered call (Back during `active`). Media teardown rides the
   *  unmount that `onDismiss` triggers. */
  const hangUp = useCallback(() => {
    void fetch(`/api/foyer/call/${encodeURIComponent(callId)}/end`, {
      method: "POST",
      headers: callHeaders(),
    }).catch(() => {});
    onDismiss(callId);
  }, [callId, onDismiss]);

  // ── Auto-answer announce window (phase 5 scaffold) ─────────────────────────
  // After `announceSeconds` we connect on our own. Back still refuses; OK may
  // answer early. A dropped socket can't strand us here - the ring's own expiry
  // (VC-18) unmounts the whole overlay.
  useEffect(() => {
    if (phase !== "announce") return;
    const secs = Math.max(0, call.announceSeconds || 0);
    const id = setTimeout(() => void answer(), secs * 1000);
    return () => clearTimeout(id);
  }, [phase, call.announceSeconds, answer]);

  // Defensive: if the session flips to `active` and it was not us who answered,
  // someone/something else took the call - stop ringing rather than leave a
  // ghost ring up. (Cannot happen for the room's sole screen, but the ring must
  // never outlive the thing it is ringing about.)
  useEffect(() => {
    if (call.state === "active" && !acted.current) {
      onDismiss(callId);
    }
  }, [call.state, callId, onDismiss]);

  // ── Remote keys ────────────────────────────────────────────────────────────
  // Capture-phase + stopImmediatePropagation so the ring OWNS OK/Back over
  // whatever scheme is mounted behind it - the scheme's own focus-grid listener
  // (also on window) never sees the key. We also steal DOM focus out of the
  // scheme scope and restore it on unmount.
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    rootRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      const k = e.key;
      const isOk = k === "Enter" || k === " ";
      const isBack = k === "Backspace" || k === "Escape";
      if (!isOk && !isBack) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (phase === "failed") {
        // Either key acknowledges the notice and returns the resident to what
        // they were watching. Both, deliberately: a resident who has just been
        // told the call did not connect should not have to find the right one.
        onDismiss(callId);
        return;
      }
      if (phase === "active" || phase === "connecting") {
        // In a live call Back hangs up; OK does nothing.
        if (isBack) hangUp();
        return;
      }
      // ring / announce: OK answers, Back declines.
      if (isBack) decline();
      else if (isOk) void answer();
    };
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      try {
        prev?.focus?.();
      } catch {
        /* previously-focused node is gone - nothing to restore */
      }
    };
  }, [phase, answer, decline, hangUp, callId, onDismiss]);

  // Belt-and-braces (mirrors OutboundCall): disarm the native capture gate on
  // any unmount path, even one that never mounted the media surface.
  useEffect(() => () => bridge.endCallSession(), []);

  // ── Render ──────────────────────────────────────────────────────────────────

  if (phase === "active" && join.current) {
    return (
      <div
        ref={rootRef}
        tabIndex={-1}
        style={{ position: "fixed", inset: 0, zIndex: Z, outline: "none" }}
      >
        <Suspense
          fallback={
            <CallVeil
              theme={t}
              label={`Ansluter till ${name}…`}
              note="Det tar bara någon sekund."
            />
          }
        >
          <CallMediaSurface
            token={join.current.token}
            url={join.current.url}
            peerName={name}
            theme={t}
            onEnded={(cause: CallEndCause) => {
              void cause;
              onDismiss(callId);
            }}
          />
        </Suspense>
        <div style={endHintPill(t)}>◀ Avsluta samtalet</div>
      </div>
    );
  }

  if (phase === "failed") {
    // The one thing this card must NOT do is blame the resident or ask them to
    // debug. It says what happened in the same voice as every other ended card
    // ("Säg till personalen, så hjälper de dig"), names the caller so they know
    // WHICH call failed, and gives them one thing they can actually do.
    return (
      <div
        ref={rootRef}
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-label={`Samtalet från ${name} kom inte fram`}
        style={scrimStyle(t)}
      >
        <div style={callCard(t)}>
          <div style={callEyebrow(t, "alert")}>Inkommande samtal</div>
          <div style={titleStyle(t)}>Samtalet kom inte fram</div>
          <div style={bodyStyle(t)}>
            Vi kunde inte koppla samtalet. Be {name} ringa upp igen, eller säg
            till personalen.
          </div>
          <div style={{ marginTop: 52 }}>
            <Button
              theme={t}
              kind="quiet"
              cap="OK"
              label="Stäng"
              onClick={() => onDismiss(callId)}
            />
          </div>
        </div>
      </div>
    );
  }

  if (phase === "connecting") {
    return (
      <div ref={rootRef} tabIndex={-1} style={{ ...scrimStyle(t), padding: 0 }}>
        <CallVeil
          theme={t}
          label={`Ansluter till ${name}…`}
          note="Det tar bara någon sekund."
        />
      </div>
    );
  }

  const announcing = phase === "announce";

  return (
    <div
      ref={rootRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={`Inkommande samtal från ${name}`}
      style={scrimStyle(t)}
    >
      <div style={callCard(t)}>
        <div style={callEyebrow(t)}>
          {announcing ? "Kopplas automatiskt" : "Inkommande samtal"}
        </div>

        <Avatar
          theme={t}
          avatarUrl={avatarUrl}
          name={call.caller.displayName}
        />

        <div style={nameStyle(t)}>{name}</div>
        {relation && <div style={relationStyle(t)}>{relation}</div>}

        {announcing ? (
          <div style={{ width: "100%", marginTop: 44 }}>
            <div
              style={{
                fontSize: CALL_TYPE.body,
                lineHeight: 1.35,
                color: t.inkSoft,
                marginBottom: 26,
              }}
            >
              Du behöver inte göra något. Samtalet öppnas strax.
            </div>
            {/* Progress bar drains over the announce window - no per-tick
                re-render, just a CSS transform. */}
            <div style={progressTrack(t)}>
              <div
                style={{
                  ...progressFill(t),
                  animation: `foyerAnnounce ${Math.max(
                    0,
                    call.announceSeconds || 0
                  )}s linear forwards`,
                }}
              />
            </div>
            <div
              style={{
                marginTop: 44,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Button
                theme={t}
                kind="quiet"
                cap="◀"
                label="Avbryt"
                onClick={decline}
              />
            </div>
          </div>
        ) : (
          <div style={buttonRow}>
            <Button
              theme={t}
              kind="answer"
              cap="OK"
              label="Svara"
              onClick={() => void answer()}
            />
            <Button
              theme={t}
              kind="quiet"
              cap="◀"
              label="Inte nu"
              onClick={decline}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes foyerRingPulse {
          0%   { transform: scale(1);    opacity: 0.6; }
          70%  { transform: scale(1.4);  opacity: 0;   }
          100% { opacity: 0; }
        }
        @keyframes foyerAnnounce { from { transform: scaleX(1); } to { transform: scaleX(0); } }
        @media (prefers-reduced-motion: reduce) {
          .foyer-ring-pulse { animation: none !important; }
        }
      `}</style>
    </div>
  );
}

// ── Pieces ────────────────────────────────────────────────────────────────────

/** The caller's face, in the care scheme's own avatar treatment (green tint,
 *  green rim, deep-green initial) - the same one CareScheme's outbound call
 *  draws, so incoming and outgoing read as one feature. */
function Avatar({
  theme: t,
  avatarUrl,
  name,
}: {
  theme: CallTheme;
  avatarUrl: string | null;
  name: string | null;
}) {
  return (
    <div
      style={{
        position: "relative",
        // 240, matching CareScheme's outbound CallAvatar exactly - the same
        // face at the same size whichever way the call is going.
        width: 240,
        height: 240,
        margin: "0 auto",
      }}
    >
      <span
        className="foyer-ring-pulse"
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 999,
          border: `5px solid ${t.green}`,
          animation: "foyerRingPulse 1.8s ease-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 999,
          overflow: "hidden",
          background: t.greenTint,
          border: `5px solid ${t.green}`,
          display: "grid",
          placeItems: "center",
          color: t.greenDeep,
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <span style={{ fontSize: 120, fontWeight: 700, lineHeight: 1.05 }}>
            {initialOf(name)}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * The two buttons on the ring.
 *
 * `answer` is the care scheme's selected-pill treatment - solid green, white
 * ink - and it is the ONLY loud thing on the card, so the obvious action is
 * unmistakable from a chair. `quiet` is the scheme's unselected pill: paper,
 * ink, hairline. Declining used to be a saturated red gradient, which shouted
 * as loudly as answering and read as danger; saying "not now" to your daughter
 * is not a destructive action. (The one red left in the call flow is the
 * live-camera chip, which is a privacy affordance - see CallMediaSurface.)
 *
 * The key cap is part of the button because the remote, not the pointer, is how
 * this is actually operated.
 */
function Button({
  theme: t,
  kind,
  cap,
  label,
  onClick,
}: {
  theme: CallTheme;
  kind: "answer" | "quiet";
  cap: string;
  label: string;
  onClick: () => void;
}) {
  const answer = kind === "answer";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 22,
        padding: "28px 48px",
        borderRadius: 999,
        border: `1px solid ${answer ? t.green : t.hairSoft}`,
        cursor: "pointer",
        background: answer ? t.green : t.paper,
        color: answer ? "#fff" : t.ink,
        fontFamily: t.sans,
        fontSize: CALL_TYPE.action,
        fontWeight: 700,
        letterSpacing: "-0.01em",
        lineHeight: 1.2,
        boxShadow: answer
          ? `0 0 0 6px ${t.surface}, 0 0 0 14px ${t.green}, 0 34px 70px -30px rgba(31,61,42,0.55)`
          : "none",
      }}
    >
      <span aria-hidden="true" style={{ display: "inline-flex" }}>
        {answer ? <PhoneIcon /> : <PhoneOffIcon />}
      </span>
      <span>{label}</span>
      <span
        style={{
          fontFamily: t.mono,
          fontSize: CALL_TYPE.cap,
          fontWeight: 700,
          lineHeight: 1.3,
          padding: "8px 16px",
          borderRadius: 10,
          background: answer ? "rgba(255,255,255,0.18)" : t.surface,
          border: `1px solid ${answer ? "rgba(255,255,255,0.4)" : t.hair}`,
          color: answer ? "#fff" : t.ink,
        }}
      >
        {cap}
      </span>
    </button>
  );
}

const iconStroke = {
  fill: "none" as const,
  stroke: "currentColor",
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};
function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" width={44} height={44} {...iconStroke}>
      <path d="M6.5 3.5l2.2.4 1.1 3.2-1.8 1.4a11 11 0 0 0 4.1 4.1l1.4-1.8 3.2 1.1.4 2.2a2 2 0 0 1-2 2.3A14 14 0 0 1 4.2 5.5a2 2 0 0 1 2.3-2z" />
    </svg>
  );
}
function PhoneOffIcon() {
  return (
    <svg viewBox="0 0 24 24" width={44} height={44} {...iconStroke}>
      <path d="M6.5 3.5l2.2.4 1.1 3.2-1.8 1.4a11 11 0 0 0 4.1 4.1l1.4-1.8 3.2 1.1.4 2.2a2 2 0 0 1-2 2.3A14 14 0 0 1 4.2 5.5a2 2 0 0 1 2.3-2z" />
      <path d="M3 3l18 18" />
    </svg>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

// Above every scheme AND any live-TV / native surface layered under the WebView.
const Z = 2147483000;

/** The one thing here that is NOT the care scheme: an opaque backdrop, because
 *  a ring has to be legible over a photo slideshow, live TV or a whiteboard. */
const scrimStyle = (t: CallTheme): CSSProperties => ({
  position: "fixed",
  inset: 0,
  zIndex: Z,
  background: t.scrim,
  display: "grid",
  placeItems: "center",
  // 60, not 80: the announce variant carries an extra body line, a progress bar
  // and a button, and at 1080p the taller card wanted the headroom back.
  padding: 60,
  outline: "none",
  fontFamily: t.sans,
});

const titleStyle = (t: CallTheme): CSSProperties => ({
  fontSize: CALL_TYPE.title,
  fontWeight: 700,
  letterSpacing: "-0.03em",
  lineHeight: 1.1,
  color: t.ink,
});

const nameStyle = (t: CallTheme): CSSProperties => ({
  fontSize: CALL_TYPE.name,
  fontWeight: 700,
  letterSpacing: "-0.035em",
  // 1.1, not the old 1.02: "Göran" and "Ljung" must keep their descenders.
  lineHeight: 1.1,
  color: t.ink,
  marginTop: 44,
});

const relationStyle = (t: CallTheme): CSSProperties => ({
  fontSize: CALL_TYPE.relation,
  lineHeight: 1.3,
  color: t.inkSoft,
  marginTop: 12,
});

const bodyStyle = (t: CallTheme): CSSProperties => ({
  fontSize: CALL_TYPE.body,
  lineHeight: 1.4,
  color: t.inkSoft,
  marginTop: 24,
  // ~40 characters a line: past that, a reader tracking back from the end of a
  // line on a TV across a room starts losing which line they were on.
  maxWidth: "20em",
  marginLeft: "auto",
  marginRight: "auto",
});

const buttonRow: CSSProperties = {
  display: "flex",
  justifyContent: "center",
  gap: 40,
  marginTop: 60,
  flexWrap: "wrap",
};

const progressTrack = (t: CallTheme): CSSProperties => ({
  height: 14,
  borderRadius: 999,
  background: t.hairSoft,
  overflow: "hidden",
});

const progressFill = (t: CallTheme): CSSProperties => ({
  height: "100%",
  width: "100%",
  transformOrigin: "left center",
  background: t.green,
  borderRadius: 999,
});

/** "◀ Avsluta samtalet", over the live video. Same pill as CallMediaSurface's
 *  name label and CareScheme's outbound hint - one geometry for the chrome. */
const endHintPill = (t: CallTheme): CSSProperties => ({
  ...videoPill(t),
  position: "absolute",
  left: "50%",
  bottom: 40,
  transform: "translateX(-50%)",
  fontSize: CALL_TYPE.hint,
});
